import { useCallback, useRef } from "react";

/**
 * Reloading the client profile after a bulk upload, once the modal that ran it has closed.
 *
 * The client shell (AddClient) owns the profile fetch and re-runs it on the
 * `refresh-client-profile` event, which is also what restores the assignment state a bare
 * re-fetch would drop. But it swaps the whole section for a spinner while it reloads, so
 * firing the event while the modal is still open tears the modal down on top of the upload
 * summary the user is reading. Hence the wait.
 *
 * Returns `markImported` for the modal's `onImported`, and `refreshOnClose` to call from
 * its `onClose` - a close with no import behind it asks for nothing, since a needless
 * reload would also discard unsaved edits in the section.
 */
export const useDeferredProfileRefresh = () => {
  const importedRef = useRef(false);

  const markImported = useCallback(() => {
    importedRef.current = true;
  }, []);

  const refreshOnClose = useCallback(() => {
    if (!importedRef.current) return;
    importedRef.current = false;
    globalThis.dispatchEvent(new Event("refresh-client-profile"));
  }, []);

  return { markImported, refreshOnClose };
};
